// EventLayer (runtime) — a Tone.Part of note events that re-fires an
// instrument every loop pass (docs/04 §4.5). No audio is recorded; the events
// are the source of truth, so the layer is always perfectly in time and
// re-quantizable.

import * as Tone from "tone";
import type { Instrument } from "../instruments/types.ts";
import type { NoteEvent } from "../util/types.ts";

interface PartEvent {
  time: Tone.Unit.Time;
  ev: NoteEvent;
}

export class EventLayer {
  private readonly part: Tone.Part<PartEvent>;

  /**
   * @param events  onsets in seconds relative to loop start
   * @param loopEnd loop length in Tone time (e.g. "4m")
   */
  constructor(instrument: Instrument, events: readonly NoteEvent[], loopEnd: Tone.Unit.Time) {
    // Store each onset in musical time (bars:beats:sixteenths) so a later
    // tempo change re-times the layer automatically (docs/04 §4.2). Sort by
    // onset and nudge any events sharing a time so no two fire at the exact
    // same transport time — a mono synth throws "start time must be strictly
    // greater than previous" and drops the note otherwise (a recorded chord on
    // a mono voice, or near-simultaneous taps). A few ms of spread is inaudible.
    const sorted = [...events].sort((a, b) => a.time - b.time);
    let lastT = -1;
    const partEvents: PartEvent[] = sorted.map((ev) => {
      let t = ev.time;
      if (t <= lastT) t = lastT + 0.003;
      lastT = t;
      return { time: Tone.Time(t).toBarsBeatsSixteenths(), ev };
    });

    this.part = new Tone.Part<PartEvent>((time, value) => {
      instrument.trigger(value.ev, time);
    }, partEvents);
    this.part.loop = true;
    this.part.loopStart = 0;
    this.part.loopEnd = loopEnd;
  }

  start(time: Tone.Unit.Time = 0): void {
    this.part.start(time);
  }

  stop(): void {
    this.part.stop();
  }

  dispose(): void {
    this.part.stop();
    this.part.dispose();
  }
}

// --- AudioLayer (mic recordings) -----------------------------------------

export interface AudioLayerBuild {
  layer: AudioLayer;
  peaks: number[];
  durationSec: number;
}

/**
 * AudioLayer — a recorded buffer looped in sync with the transport (docs/04
 * §4.6). The recorded head is trimmed by the latency offset so the downbeat
 * lands on the grid, and the buffer is fit to exactly one loop length.
 */
export class AudioLayer {
  private constructor(private readonly player: Tone.Player) {}

  static async fromBlob(
    blob: Blob,
    opts: { loopDurSec: number; latencyOffsetSec: number; output: Tone.ToneAudioNode },
  ): Promise<AudioLayerBuild> {
    const arrayBuf = await blob.arrayBuffer();
    const rawCtx = Tone.getContext().rawContext as unknown as AudioContext;
    const decoded = await rawCtx.decodeAudioData(arrayBuf);
    const fitted = trimAndFit(rawCtx, decoded, opts.latencyOffsetSec, opts.loopDurSec);

    const player = new Tone.Player(new Tone.ToneAudioBuffer(fitted));
    player.loop = true;
    player.loopStart = 0;
    player.loopEnd = opts.loopDurSec;
    player.connect(opts.output);

    return { layer: new AudioLayer(player), peaks: computePeaks(fitted, 48), durationSec: opts.loopDurSec };
  }

  start(time: Tone.Unit.Time = 0): void {
    this.player.sync().start(time);
  }

  stop(): void {
    this.player.stop();
  }

  dispose(): void {
    this.player.unsync();
    this.player.stop();
    this.player.dispose();
  }
}

/** Slice `loopDurSec` of audio starting `latencyOffsetSec` into the buffer. */
function trimAndFit(
  ctx: AudioContext,
  buf: AudioBuffer,
  latencyOffsetSec: number,
  loopDurSec: number,
): AudioBuffer {
  const sr = buf.sampleRate;
  const offset = Math.max(0, Math.floor(latencyOffsetSec * sr));
  const len = Math.max(1, Math.floor(loopDurSec * sr));
  const out = ctx.createBuffer(buf.numberOfChannels, len, sr);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const src = buf.getChannelData(c);
    const dst = out.getChannelData(c);
    for (let i = 0; i < len; i++) {
      const j = offset + i;
      dst[i] = j < src.length ? (src[j] ?? 0) : 0;
    }
  }
  return out;
}

/** Downsample to `n` peak magnitudes (0..1) for a lane waveform. */
function computePeaks(buf: AudioBuffer, n: number): number[] {
  const ch = buf.getChannelData(0);
  const bucket = Math.max(1, Math.floor(ch.length / n));
  const peaks: number[] = [];
  for (let i = 0; i < n; i++) {
    let peak = 0;
    const start = i * bucket;
    for (let j = start; j < start + bucket && j < ch.length; j++) {
      const a = Math.abs(ch[j] ?? 0);
      if (a > peak) peak = a;
    }
    peaks.push(Math.min(1, peak));
  }
  return peaks;
}
